import { useEffect, useState } from "react";
import api from "../api";
import { Lifebuoy, BookOpen, ArrowSquareOut, SpinnerGap } from "@phosphor-icons/react";

export default function DreamsSupport() {
  const [services, setServices] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, a] = await Promise.all([api.get("/support/services"), api.get("/support/articles")]);
        if (!cancelled) { setServices(s.data.data || []); setArticles(a.data.data || []); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <p className="text-[#978D78] flex items-center gap-2"><SpinnerGap size={16} className="animate-spin" /> A carregar...</p>;

  return (
    <div className="space-y-12 pb-12">
      <header>
        <p className="text-xs uppercase tracking-widest text-[#6F4F1D]/80">Apoio</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Suporte <span className="text-[#6F4F1D]">+</span> Artigos</h1>
        <p className="text-[#4B5563] mt-3 max-w-xl text-sm">Recursos curados para acelerar a tua jornada de sonhador.</p>
      </header>

      <section>
        <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
          <Lifebuoy size={18} className="text-[#6F4F1D]" weight="fill" /> Serviços de apoio
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map(s => (
            <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer"
               data-testid={`service-${s.id}`}
               className="rounded-2xl border border-[#E6E1D6] bg-white/[0.02] p-5 hover:border-[#6F4F1D]/30 hover:bg-white transition-all group">
              <h3 className="text-base font-semibold">{s.title}</h3>
              <p className="text-xs text-[#4B5563] mt-2 leading-relaxed">{s.description}</p>
              <span className="inline-flex items-center gap-1 mt-4 text-xs text-[#6F4F1D] group-hover:text-white">
                Visitar <ArrowSquareOut size={11} />
              </span>
            </a>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
          <BookOpen size={18} className="text-[#6F4F1D]" weight="fill" /> Artigos de apoio
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {articles.map(a => (
            <article key={a.id}
                     data-testid={`article-${a.id}`}
                     onClick={() => setSelected(a)}
                     className="rounded-xl border border-[#E6E1D6] bg-white/[0.02] p-4 hover:border-[#6F4F1D]/30 hover:bg-white transition-all cursor-pointer">
              <span className="text-[10px] uppercase tracking-widest text-[#6F4F1D]/80">{a.tag}</span>
              <h3 className="text-sm font-semibold mt-1">{a.title}</h3>
              <p className="text-xs text-[#4B5563] mt-1 line-clamp-3">{a.summary}</p>
            </article>
          ))}
        </div>
      </section>

      {selected && <ArticleModal id={selected.id} onClose={() => setSelected(null)} />}
    </div>
  );
}

function ArticleModal({ id, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/support/articles/${id}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Render markdown muito simples (negrito, títulos, listas)
  const renderMd = (md) => {
    if (!md) return null;
    return md.split("\n").map((line, i) => {
      if (line.startsWith("### ")) return <h3 key={i} className="text-base font-semibold mt-4 mb-1 text-[#6F4F1D]">{line.slice(4)}</h3>;
      if (line.startsWith("## "))  return <h2 key={i} className="text-lg font-bold mt-5 mb-2">{line.slice(3)}</h2>;
      if (line.startsWith("# "))   return <h1 key={i} className="text-xl font-bold mt-2 mb-3">{line.slice(2)}</h1>;
      if (line.match(/^\s*[-*]\s/)) return <li key={i} className="ml-5 text-[#0F1829] text-sm">{line.replace(/^\s*[-*]\s/, "")}</li>;
      if (/^\d+\.\s/.test(line))   return <li key={i} className="ml-5 list-decimal text-[#0F1829] text-sm">{line.replace(/^\d+\.\s/, "")}</li>;
      if (!line.trim()) return <div key={i} className="h-2" />;
      const formatted = line.split(/(\*\*[^*]+\*\*)/g).map((s, j) =>
        s.startsWith("**") && s.endsWith("**")
          ? <strong key={j} className="text-[#6F4F1D]">{s.slice(2, -2)}</strong>
          : <span key={j}>{s}</span>
      );
      return <p key={i} className="text-[#0F1829] text-sm leading-relaxed my-1.5">{formatted}</p>;
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="article-modal"
           className="bg-[#F5F2EB] border border-[#E6E1D6] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <header className="sticky top-0 bg-[#F5F2EB] border-b border-[#E6E1D6] px-6 py-3 flex items-center justify-between z-10">
          <span className="text-[10px] uppercase tracking-widest text-[#6F4F1D]/80">{data?.meta?.tag}</span>
          <button onClick={onClose} className="text-[#978D78] hover:text-white text-xs">Fechar</button>
        </header>
        <div className="p-6">
          {loading && <p className="text-[#978D78] flex items-center gap-2"><SpinnerGap size={14} className="animate-spin" /> A gerar conteúdo com IA...</p>}
          {err && <p className="text-rose-300 text-sm">{err}</p>}
          {data && <>
            <h1 className="text-2xl font-bold mb-3">{data.meta.title}</h1>
            <div className="prose-invert">{renderMd(data.conteudo)}</div>
          </>}
        </div>
      </div>
    </div>
  );
}
